export interface ParsedError {
  message: string;
  code?: string;
  details?: any;
  httpStatus?: number;
  hint?: string;
  functionName?: string;
  requestBody?: any;
  responseBody?: any;
  stack?: string;
  originalError: any;
}

/**
 * Parse any error into a structured format with all available details
 * Handles Supabase errors, Edge Function errors, HTTP errors, and custom errors
 */
export function parseError(error: any, context?: any): ParsedError {
  const parsed: ParsedError = {
    message: error?.message || String(error),
    originalError: error
  };

  // Supabase database errors (PostgrestError)
  if (error?.code && error?.details) {
    parsed.code = error.code;
    parsed.details = error.details;
    parsed.hint = error.hint;
  }

  // Supabase Functions errors (FunctionsHttpError, FunctionsRelayError, FunctionsFetchError)
  if (error?.context) {
    const url = error.context?.url || '';
    parsed.functionName = url.split('/').pop();
    parsed.httpStatus = error.context?.status;
    
    // Try to parse response body if it's a string
    let responseBody = error.context?.body;
    if (typeof responseBody === 'string') {
      try {
        responseBody = JSON.parse(responseBody);
      } catch {
        // Keep as string if not valid JSON
      }
    }
    parsed.responseBody = responseBody;
  }

  // HTTP errors with status
  if (error?.status && !parsed.httpStatus) {
    parsed.httpStatus = error.status;
  }

  // Custom error codes (SharePoint, etc) - prioritize specific codes
  if (error?.code && !parsed.code) {
    parsed.code = error.code;
  }

  // Stack trace
  if (error?.stack) {
    parsed.stack = error.stack;
  }

  // Request context from additional context parameter
  if (context?.requestBody) {
    parsed.requestBody = context.requestBody;
  }

  // Additional error properties that might exist
  if (error?.statusCode && !parsed.httpStatus) {
    parsed.httpStatus = error.statusCode;
  }

  if (error?.errorCode && !parsed.code) {
    parsed.code = error.errorCode;
  }

  return parsed;
}

/**
 * Generate a detailed, human-readable error message for display in UI
 */
export function getDetailedErrorMessage(error: any): string {
  const parsed = parseError(error);
  
  let msg = parsed.message;
  
  if (parsed.code) {
    msg += ` (Code: ${parsed.code})`;
  }
  
  if (parsed.httpStatus) {
    msg += ` [HTTP ${parsed.httpStatus}]`;
  }
  
  if (parsed.functionName) {
    msg += `\nFunctie: ${parsed.functionName}`;
  }
  
  if (parsed.details) {
    const detailsStr = typeof parsed.details === 'string' 
      ? parsed.details 
      : JSON.stringify(parsed.details, null, 2);
    msg += `\nDetails: ${detailsStr}`;
  }
  
  if (parsed.hint) {
    msg += `\n💡 Tip: ${parsed.hint}`;
  }

  if (parsed.responseBody && typeof parsed.responseBody === 'object') {
    const errorMsg = parsed.responseBody.error || parsed.responseBody.message;
    if (errorMsg && !msg.includes(errorMsg)) {
      msg += `\nResponse: ${errorMsg}`;
    }
  }
  
  return msg;
}

/**
 * Get HTTP status category for styling
 */
export function getHttpStatusCategory(status?: number): 'success' | 'client_error' | 'server_error' | 'unknown' {
  if (!status) return 'unknown';
  if (status >= 200 && status < 300) return 'success';
  if (status >= 400 && status < 500) return 'client_error';
  if (status >= 500) return 'server_error';
  return 'unknown';
}
